import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getTaskById, updateTask } from "../../services/taskService";
import { getProjects } from "../../services/projectService";
import { getUsers } from "../../services/userService";

const EditTask = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const goBack = () => {
    navigate(-1);
  };

  const [taskData, setTaskData] = useState({
    name: "",
    description: "",
    project_id: "",
    assigned_to: "",
    priority: "Medium",
    status: "Pending",
    deadline: "",
  });

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  /* 🔹 NEW: attachments state */
  const [files, setFiles] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ================= LOAD DATA ================= */

  useEffect(() => {
    async function fetchData() {
      try {
        const [task, projectsList, usersList] = await Promise.all([
          getTaskById(id),
          getProjects(),
          getUsers(),
        ]);

        setTaskData({
          ...task,
          assigned_to: task.assignTo?.id || task.assigned_to,
        });

        setProjects(projectsList);
        setUsers(usersList);
      } catch (err) {
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    setTaskData({ ...taskData, [e.target.name]: e.target.value });
  };

  /* 🔹 NEW: file handler */
  const handleFileChange = (e) => {
    setFiles(e.target.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const formData = new FormData();

      Object.keys(taskData).forEach((key) => {
        formData.append(key, taskData[key]);
      });

      formData.append("action", "update");
      formData.append("id", id);

      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          formData.append("attachments[]", files[i]);
        }
      }

      let res = await updateTask(formData);
      console.log(res);
      goBack();
    } catch (err) {
      console.error(err);
      setError("Update failed.");
    }
  };

  if (loading)
    return <div className="text-center text-gray-600">Loading...</div>;

  if (error) return <div className="text-center text-red-500">{error}</div>;

  return (
    <>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Edit Task</h1>
          <p className="text-sm text-gray-500">
            Update task details and assignment
          </p>
        </div>

        <Link
          onClick={goBack}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back
        </Link>
      </div>

      {/* FORM */}
      <div className="bg-white rounded-xl shadow-sm p-6 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Project */}
          <div>
            <label className="block mb-1 font-medium">Project</label>
            <select
              name="project_id"
              value={taskData.project_id}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            >
              <option value="">Select Project</option>
              {projects.map((proj) => (
                <option key={proj.id} value={proj.id}>
                  {proj.name}
                </option>
              ))}
            </select>
          </div>
          {/* Title */}
          <div>
            <label className="block mb-1 font-medium">Title</label>
            <input
              type="text"
              name="name"
              value={taskData.name}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block mb-1 font-medium">Description</label>
            <textarea
              name="description"
              value={taskData.description}
              onChange={handleChange}
              rows="4"
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {/* 🔹 NEW: Attachments */}
          <div>
            <label className="block mb-1 font-medium">Attachments</label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full border px-3 py-2 rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Upload screenshots, docs, or files
            </p>
          </div>

          {/* GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Assign To */}
            <div>
              <label className="block mb-1 font-medium">Assign To</label>
              <select
                name="assigned_to"
                value={taskData.assigned_to}
                onChange={handleChange}
                required
                className="w-full border px-3 py-2 rounded"
              >
                <option value="">Select User</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block mb-1 font-medium">Priority</label>
              <select
                name="priority"
                value={taskData.priority}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block mb-1 font-medium">Status</label>
              <select
                name="status"
                value={taskData.status}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
              </select>
            </div>
            {/* Due Date */}
            <div>
              <label className="block mb-1 font-medium">Due Date</label>
              <input
                type="date"
                name="deadline"
                value={taskData.deadline}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          {/* ACTION */}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
          >
            Update Task
          </button>
        </form>
      </div>
    </>
  );
};

export default EditTask;
